/**
 * HorizontalCreatorRow Component
 *
 * Netflix-style horizontal scrolling row for featured creators.
 */

import type { ReactElement } from 'react';
import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Users, User as UserIcon, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export interface HorizontalCreator {
  user_id: string;
  name: string;
  handle?: string;
  picture?: string;
  bio?: string;
  followers_count: number;
  story_count: number;
  is_premium: boolean;
  is_following: boolean;
}

interface HorizontalCreatorRowProps {
  title: string;
  creators: HorizontalCreator[];
  loading?: boolean;
  onSeeAll?: () => void;
  onCreatorClick?: (creatorId: string) => void;
  onFollowToggle?: (creatorId: string, e: React.MouseEvent) => void;
}

// Helper to get full image URL
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HorizontalCreatorRow = ({
  title,
  creators,
  loading = false,
  onSeeAll,
  onCreatorClick,
  onFollowToggle,
}: HorizontalCreatorRowProps): ReactElement | null => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollPosition = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [creators]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = 350;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleCreatorClick = (creatorId: string) => {
    if (onCreatorClick) {
      onCreatorClick(creatorId);
    } else {
      navigate(`/user/${creatorId}`);
    }
  };

  // Don't render if no creators and not loading
  if (!loading && creators.length === 0) {
    return null;
  }

  return (
    <div className="relative group/row">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <h3
          className="font-bold text-base flex items-center gap-2"
          style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
        >
          <Users className="w-5 h-5" style={{ color: '#10B981' }} />
          {title}
        </h3>
        {onSeeAll && (
          <button
            onClick={onSeeAll}
            className="text-xs font-semibold hover:underline flex items-center gap-1"
            style={{ fontFamily: 'Quicksand, sans-serif', color: '#10B981' }}
          >
            See all
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-all duration-200 hover:scale-110"
            style={{
              background: 'var(--st-bg-surface)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid var(--st-border-light)',
            }}
          >
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--st-text-heading)' }} />
          </button>
        )}

        {/* Creators Row */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 horizontal-scroll-row"
          onScroll={checkScrollPosition}
        >
          {loading ? (
            // Loading Skeletons
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex-shrink-0 w-[150px] h-[140px] rounded-xl animate-pulse"
                style={{ background: 'var(--st-border-light)' }}
              />
            ))
          ) : (
            creators.map((creator) => (
              <div
                key={creator.user_id}
                onClick={() => handleCreatorClick(creator.user_id)}
                className="flex-shrink-0 w-[150px] rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.03] hover:shadow-lg p-3 flex flex-col items-center"
                style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
              >
                {/* Avatar */}
                <div
                  className="w-14 h-14 rounded-full overflow-hidden mb-2 flex-shrink-0"
                  style={{ background: 'var(--st-bg-muted)' }}
                >
                  {creator.picture ? (
                    <img
                      src={getImageUrl(creator.picture) || undefined}
                      alt={creator.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserIcon className="w-7 h-7" style={{ color: 'var(--st-text-muted)' }} />
                    </div>
                  )}
                </div>

                {/* Name + Premium Badge */}
                <div className="flex items-center justify-center gap-1 mb-1">
                  <h4
                    className="font-bold text-xs text-center truncate max-w-[100px]"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    {creator.name}
                  </h4>
                  {creator.is_premium && (
                    <Crown className="w-3 h-3 flex-shrink-0" style={{ color: '#9B59B6' }} />
                  )}
                </div>

                {/* Stats */}
                <p
                  className="text-[9px] mb-2"
                  style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                >
                  {creator.story_count} {creator.story_count === 1 ? 'story' : 'stories'}
                </p>

                {/* Follow Button */}
                <button
                  onClick={(e) => onFollowToggle?.(creator.user_id, e)}
                  className="px-4 py-1.5 rounded-full text-[10px] font-semibold transition-all flex-shrink-0"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    background: creator.is_following ? 'var(--st-bg-muted)' : 'var(--st-primary)',
                    color: creator.is_following ? 'var(--st-text-heading)' : 'white',
                  }}
                >
                  {creator.is_following ? 'Following' : 'Follow'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HorizontalCreatorRow;
